import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { tokens } from '@redbax/shared';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';

interface TimelineBarProps {
  data: Array<{ timestamp: string; count: number }>;
  title?: string;
  loading?: boolean;
  granularity?: string;
  headerAction?: React.ReactNode;
}

function formatTick(timestamp: string, granularity: string): string {
  const d = new Date(timestamp);
  switch (granularity) {
    case '5min':
    case '10min':
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'hour':
    case '6hour':
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' }) +
        ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    case 'day':
    case 'week':
      return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
    default:
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
}

function formatTooltip(timestamp: string, granularity: string): string {
  const d = new Date(timestamp);
  switch (granularity) {
    case '5min':
    case '10min':
      return d.toLocaleString([], {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    case 'hour':
    case '6hour':
      return d.toLocaleString([], {
        month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    default:
      return d.toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' });
  }
}

function tickInterval(dataLength: number): number | undefined {
  if (dataLength <= 30) return 0;
  if (dataLength <= 60) return 1;
  if (dataLength <= 150) return 4;
  return Math.floor(dataLength / 20);
}

export function TimelineBar({ data, title = 'Event Timeline', loading, granularity = 'hour', headerAction }: TimelineBarProps) {
  const header = (
    <CardHeader className="flex flex-row items-center justify-between space-y-0">
      <CardTitle className="text-base">{title}</CardTitle>
      {headerAction && <div>{headerAction}</div>}
    </CardHeader>
  );

  if (loading) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="h-[300px] animate-pulse rounded bg-surface-raised" />
        </CardContent>
      </Card>
    );
  }

  if (data.length === 0) {
    return (
      <Card>
        {header}
        <CardContent>
          <div className="flex h-[300px] items-center justify-center text-sm text-text-secondary">
            No timeline data available
          </div>
        </CardContent>
      </Card>
    );
  }

  const interval = tickInterval(data.length);

  return (
    <Card>
      {header}
      <CardContent>
        <div className="h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} barCategoryGap="1%">
              <CartesianGrid strokeDasharray="3 3" stroke={tokens.colors.border} vertical={false} />
              <XAxis
                dataKey="timestamp"
                stroke={tokens.colors.textSecondary}
                fontSize={11}
                tickFormatter={(v) => formatTick(v, granularity)}
                interval={interval}
                angle={-45}
                textAnchor="end"
                height={60}
              />
              <YAxis
                stroke={tokens.colors.textSecondary}
                fontSize={11}
                allowDecimals={false}
              />
              <Tooltip
                labelFormatter={(v) => formatTooltip(v as string, granularity)}
                contentStyle={{
                  backgroundColor: tokens.colors.surface,
                  border: `1px solid ${tokens.colors.border}`,
                  borderRadius: '6px',
                  color: tokens.colors.textPrimary,
                  fontSize: 13,
                }}
              />
              <Bar
                dataKey="count"
                fill={tokens.colors.redAccent}
                radius={[2, 2, 0, 0]}
                maxBarSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}
